﻿import {DefaultErrorFallback} from "@2d-front-monorepo/design-library";
import {JSX, Suspense, useCallback} from "react";
import {ErrorBoundary} from "react-error-boundary";
import {Route, Routes, useNavigate} from "react-router";
import AsyncInitializer from "./components/AsyncInitializer";
import NavigateToDefault from "./components/NavigateToDefault";
import PermitsLoading from "./components/PermitsLoading";
import PrivateRoute from "./components/PrivateRoute";
import {GlobalContext} from "./contexts/GlobalContext";
import {UserProvider} from "./contexts/UserContext";
import {reportError} from "./functions";
import {MainLayout} from "./layouts/MainLayout";
import {MenuLayout} from "./layouts/MenuLayout";
import ForgotPassword from "./pages/auth/ForgotPassword";
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import Documents from "./pages/documents/Documents";
import DocumentUpdate from "./pages/documents/DocumentUpdate";
import CreateDriver from "./pages/drivers/CreateDriver";
import ViewDriver from "./pages/drivers/ViewDriver";
import Invoices from "./pages/invoices/Invoices";
import NotFound from "./pages/NotFound";
import Payments from "./pages/payments/Payments";
import PermitsGroup from "./pages/permits/Groups";
import Home from "./pages/permits/Home";
import PermitList from "./pages/permits/PermitList";
import UpdateDocumentation from "./pages/permits/UpdateDocumentation";
import ViewPermit from "./pages/permits/ViewPermit";
import CreateResidency from "./pages/residencies/CreateResidency";
import ViewResidency from "./pages/residencies/ViewResidency";
import Settings from "./pages/settings/Settings";
import CreateVehicle from "./pages/vehicles/CreateVehicle";
import EditVehicle from "./pages/vehicles/EditVehicle";
import Vehicles from "./pages/vehicles/Vehicles";
import ViewVehicle from "./pages/vehicles/ViewVehicle";
import Welcome from "./pages/Welcome";
import {getWelcomeRoutes} from "./routes";
import {APP_VERSION} from "./version";

function logErrorToServer(error: Error): void {
	reportError(error);
}

console.info(`%cPermits-UI - Version ${APP_VERSION}`, "color: blue;");

export default function Application(): JSX.Element {
	const navigate = useNavigate();

	const restartApplication = useCallback(() => {
		GlobalContext.reset();
		navigate("/");
	}, []);

	return (
		<ErrorBoundary FallbackComponent={DefaultErrorFallback} onReset={restartApplication} onError={logErrorToServer}>
			<AsyncInitializer loading={<PermitsLoading title="Loading application configuration..."/>}>
				<UserProvider>
					<Routes>
						<Route path="/" element={<MainLayout/>}>
							<Route index element={<NavigateToDefault/>}/>
							<Route path="welcome" element={<Welcome/>}/>

							<Route path="auth">
								<Route path="signin" element={<SignIn/>}/>
								<Route path="signup" element={<SignUp/>}/>
								<Route path="forgot-password" element={<ForgotPassword/>}/>
							</Route>

							<Route element={<PrivateRoute/>}>
								<Route element={<MenuLayout/>}>
									<Route path="home/*" element={
										<Suspense fallback={<PermitsLoading/>}>
											<Home/>
										</Suspense>}
									/>

									<Route path="permits">
										<Route index element={<PermitList/>}/>
										<Route path="groups/:groupName" element={<PermitsGroup/>}/>
										<Route path=":permitId" element={<ViewPermit/>}/>
										<Route path=":permitId/documentation-update" element={<UpdateDocumentation/>}/>
									</Route>

									<Route path="drivers">
										<Route path="create" element={
											<Suspense fallback={<PermitsLoading/>}>
												<CreateDriver/>
											</Suspense>}
										/>
										<Route path=":driverId" element={<ViewDriver/>}/>
									</Route>

									<Route path="residencies">
										<Route path="create" element={
											<Suspense fallback={<PermitsLoading/>}>
												<CreateResidency/>
											</Suspense>}
										/>
										<Route path=":residencyId" element={<ViewResidency/>}/>
									</Route>

									<Route path="vehicles">
										<Route index element={<Vehicles/>}/>
										<Route path="create" element={
											<Suspense fallback={<PermitsLoading/>}>
												<CreateVehicle/>
											</Suspense>}
										/>
										<Route path=":vehicleId/edit" element={<EditVehicle/>}/>
										<Route path=":vehicleId" element={<ViewVehicle/>}/>
									</Route>

									<Route path="payments" element={<Payments/>}/>

									<Route path="documents">
										<Route index element={<Documents/>}/>
										<Route path=":documentId/update" element={<DocumentUpdate/>}/>
									</Route>

									<Route path="invoices" element={<Invoices/>}/>

									<Route path="settings" element={<Settings/>}/>
								</Route>
							</Route>

							{getWelcomeRoutes().map(route => (
								<Route path={route.path} element={route.component}/>
							))}
						</Route>
						<Route path="*" element={<NotFound/>}/>
					</Routes>
				</UserProvider>
			</AsyncInitializer>
		</ErrorBoundary>
	)
}
